import { ACQUISITION_SOURCES, type AcquisitionSource } from '@arabat/contracts';
import { useState } from 'react';
import type { Language } from './content.js';

/**
 * "How did you hear about us?", the referral link (`?ref=`) and the promo code (`?code=` or
 * typed) on the trial forms (ADR-0152, BR-256). Copy lives here so the sign-up pages stay small.
 */
const COPY = {
  ar: {
    heard: 'كيف عرفت عن عربات؟',
    choose: 'اختر (اختياري)',
    other: 'اكتبها لنا',
    promo: 'كود العرض (اختياري)',
    referred: 'أنت مدعو من أحد عملاء عربات 🎉',
    promoInvalid: 'كود العرض غير صالح أو انتهى. صحّحه أو احذفه ثم أعد المحاولة.',
    sources: {
      instagram: 'إنستغرام',
      tiktok: 'تيك توك',
      google: 'بحث جوجل',
      referral: 'صديق أو نشاط آخر',
      whatsapp: 'واتساب',
      field: 'زيارة من فريق عربات',
      other: 'أخرى',
    },
  },
  en: {
    heard: 'How did you hear about Arabat?',
    choose: 'Choose (optional)',
    other: 'Tell us where',
    promo: 'Promo code (optional)',
    referred: 'You were invited by an Arabat customer 🎉',
    promoInvalid: 'This promo code is not valid or has ended. Correct or remove it and try again.',
    sources: {
      instagram: 'Instagram',
      tiktok: 'TikTok',
      google: 'Google search',
      referral: 'A friend or another business',
      whatsapp: 'WhatsApp',
      field: 'A visit by the Arabat team',
      other: 'Other',
    },
  },
} as const;

const REF_KEY = 'arabat.ref';
const CODE_KEY = 'arabat.code';
const clean = (v: string | null | undefined, max: number) =>
  v
    ? v
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9_-]/g, '')
        .slice(0, max)
    : '';

/**
 * Keeps the visit's `ref` and `code` link parameters for the sign-up form, also when the visitor
 * moves from the home page to /start or /pricing. Returns what is known for this visit.
 */
export function rememberVisit(): { ref: string; code: string } {
  const params = new URLSearchParams(window.location.search);
  const ref = clean(params.get('ref'), 12);
  const code = clean(params.get('code'), 32);
  try {
    if (ref) sessionStorage.setItem(REF_KEY, ref);
    if (code) sessionStorage.setItem(CODE_KEY, code);
    return {
      ref: sessionStorage.getItem(REF_KEY) ?? '',
      code: sessionStorage.getItem(CODE_KEY) ?? '',
    };
  } catch {
    return { ref, code };
  }
}

export interface AcquisitionState {
  source: AcquisitionSource | '';
  sourceOther: string;
  promoCode: string;
  referralCode: string;
}

export function useAcquisition() {
  const [state, setState] = useState<AcquisitionState>(() => {
    const visit = rememberVisit();
    return {
      source: visit.ref ? 'referral' : '',
      sourceOther: '',
      promoCode: visit.code,
      referralCode: visit.ref,
    };
  });
  /** The `acquisition` object of the sign-up request. */
  const body = () => ({
    source: state.source || null,
    sourceOther: state.source === 'other' ? state.sourceOther.trim() || null : null,
    referralCode: state.referralCode || null,
    promoCode: state.promoCode.trim() || null,
  });
  return { state, setState, body };
}

export function promoInvalidText(lang: Language): string {
  return COPY[lang].promoInvalid;
}

export function AcquisitionFields({
  lang,
  state,
  setState,
  inputClass,
}: {
  lang: Language;
  state: AcquisitionState;
  setState: (update: (s: AcquisitionState) => AcquisitionState) => void;
  inputClass: string;
}) {
  const c = COPY[lang];
  return (
    <>
      {state.referralCode ? (
        <p className="rounded-2xl bg-brand-soft px-4 py-3 text-sm text-brand sm:col-span-2">
          {c.referred}
        </p>
      ) : null}
      <label className="block">
        <span className="text-sm font-medium text-ink">{c.heard}</span>
        <select
          name="source"
          value={state.source}
          onChange={(e) =>
            setState((s) => ({ ...s, source: e.target.value as AcquisitionSource | '' }))
          }
          className={inputClass}
        >
          <option value="">{c.choose}</option>
          {ACQUISITION_SOURCES.map((source) => (
            <option key={source} value={source}>
              {c.sources[source]}
            </option>
          ))}
        </select>
      </label>
      {state.source === 'other' ? (
        <label className="block">
          <span className="text-sm font-medium text-ink">{c.other}</span>
          <input
            name="sourceOther"
            maxLength={120}
            value={state.sourceOther}
            onChange={(e) => setState((s) => ({ ...s, sourceOther: e.target.value }))}
            className={inputClass}
          />
        </label>
      ) : null}
      <label className="block">
        <span className="text-sm font-medium text-ink">{c.promo}</span>
        <input
          name="promoCode"
          maxLength={32}
          dir="ltr"
          autoComplete="off"
          value={state.promoCode}
          onChange={(e) =>
            setState((s) => ({ ...s, promoCode: e.target.value.toUpperCase().slice(0, 32) }))
          }
          className={`${inputClass} text-start`}
        />
      </label>
    </>
  );
}
